/**
 * Deep Linking — handles incoming URLs when the app is opened via
 * Universal Links (iOS), App Links (Android) or the custom `alumnia://`
 * scheme.
 *
 * `initDeepLinking(router)` is mounted once by the native shell
 * (`hooks/use-native-deep-links.ts`, used by `NativeAppShell`). It handles
 * both a cold start (the link that launched the app) and a warm start (a
 * link tapped while the app is already running).
 *
 * Safety: the URL comes from outside the app, so it is never navigated to as
 * given. `deepLinkTarget` returns an in-app path (same origin by
 * construction) or null; anything else is ignored.
 */

import { isNativePlatform } from '@/lib/utils/platform'
import { isDocumentRoute, isEntryRoute } from '@/lib/navigation/routes'
import { closeInAppBrowser, nativeAuthReturnTarget } from '@/lib/native/oauth-return'

type RouterLike = {
  push: (url: string) => void
}

/** Custom scheme registered in Info.plist (CFBundleURLTypes) and AndroidManifest.xml. */
const CUSTOM_SCHEME = 'alumnia'

/**
 * Hosts whose https links the app opens: the apex domain and any of its
 * subdomains (`app.` is the one verified by AASA / assetlinks; `www.` is the
 * NEXT_PUBLIC_BASE_URL of the marketing site and of emails). Organizations
 * are paths (`/in/<slug>`), not hosts.
 */
const APEX_HOST = 'alumnia.net'

export function isAllowedDeepLinkHost(hostname: string): boolean {
  const host = hostname.toLowerCase()
  return host === APEX_HOST || host.endsWith(`.${APEX_HOST}`)
}

/** ASCII control characters, whitespace and backslashes: `new URL` would silently rewrite them. */
const UNSAFE_CHARS = /[\u0000-\u0020\u007f\\]/

/** A path (query and hash kept) that starts with exactly one slash, checked before parsing. */
function isPlainPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

/**
 * The in-app href to open for an incoming link, or null when it must be
 * ignored. Accepted forms:
 *  - `https://<alumnia.net or a subdomain>/<path>` (Universal / App Links)
 *  - `alumnia://<path>` and `alumnia://<alumnia host>/<path>` (custom scheme)
 *  - a bare `/<path>`
 * The path must be an entry route of lib/navigation/routes.ts (F8), which
 * lists the routes of both interfaces: an old link (`/chapters/…`, `?tab=`)
 * redirects once it lands. Query and `#anchor` are kept; the host is dropped,
 * so the result can only ever be a same-origin path. Everything else
 * (`javascript:`, `//evil.com`, another host, credentials in the URL, a port,
 * control characters, an `http:` link) is null.
 */
export function deepLinkTarget(url: string): string | null {
  if (typeof url !== 'string' || url.length === 0 || url.length > 2048) return null
  if (UNSAFE_CHARS.test(url)) return null

  let path: string
  if (url.startsWith('/')) {
    path = url
  } else {
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1]?.toLowerCase()
    if (scheme === CUSTOM_SCHEME) {
      // alumnia://in/org/events/1 (the path right after the scheme) or
      // alumnia://app.alumnia.net/in/org/events/1 (a host, then the path).
      const rest = url.slice(CUSTOM_SCHEME.length + 1).replace(/^\/\//, '')
      const firstSegment = rest.split(/[/?#]/, 1)[0]
      path = isAllowedDeepLinkHost(firstSegment)
        ? rest.slice(firstSegment.length) || '/'
        : rest.startsWith('/')
          ? rest
          : `/${rest}`
    } else if (scheme === 'https') {
      let parsed: URL
      try {
        parsed = new URL(url)
      } catch {
        return null
      }
      if (parsed.username || parsed.password || parsed.port) return null
      if (!isAllowedDeepLinkHost(parsed.hostname)) return null
      path = parsed.pathname + parsed.search + parsed.hash
    } else {
      return null
    }
  }

  if (!isPlainPath(path)) return null

  let parsed: URL
  try {
    // A bare path resolves against a dummy origin; only path, query and hash are used.
    parsed = new URL(path, 'https://app.invalid')
  } catch {
    return null
  }
  if (parsed.origin !== 'https://app.invalid') return null
  // An encoded slash, backslash or dot (also double-encoded) inside the path is
  // never part of a real route: it only exists to smuggle a traversal past the
  // pattern match. `%2e%2e` as a whole segment was already resolved by `URL`.
  if (/%(?:25)*(?:2f|5c|2e)/i.test(parsed.pathname)) return null
  if (!isEntryRoute(parsed.pathname)) return null
  return parsed.pathname + parsed.search + parsed.hash
}

// ---------------------------------------------------------------------------
// Listener (mounted once)
// ---------------------------------------------------------------------------

/** The same URL delivered twice (launch URL and retained event) is one navigation. */
const DUPLICATE_WINDOW_MS = 2000

let activeRouter: RouterLike | null = null
/** Which `initDeepLinking` call owns `activeRouter`: only the latest one may release it. */
let activeOwner: symbol | null = null
let pendingTarget: string | null = null
let registration: Promise<void> | null = null
let lastHandled: { url: string; at: number } | null = null
let handledAt = 0

// A link the app already acted on must not be acted on again after a page
// reload. `App.getLaunchUrl()` keeps returning the link that launched the app
// for the whole life of the process, and the module state above is lost on
// every full page load (`location.assign`), so without a mark that outlives
// the document the launch link is replayed on each load: for the LinkedIn
// return (`alumnia://auth/callback?code=…`) that is an endless loop of
// /auth/callback requests behind the loader. sessionStorage lives as long as
// the WebView, which is the lifetime we want (a killed app starts clean).
const CONSUMED_KEY = 'alumnia:deeplink:consumed'
const CONSUMED_MAX = 8
const consumedInMemory = new Set<string>()

function readConsumed(): string[] {
  try {
    const raw = window.sessionStorage.getItem(CONSUMED_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((u): u is string => typeof u === 'string') : []
  } catch {
    return []
  }
}

function wasConsumed(url: string): boolean {
  return consumedInMemory.has(url) || readConsumed().includes(url)
}

function markConsumed(url: string): void {
  consumedInMemory.add(url)
  try {
    const next = [...readConsumed().filter((u) => u !== url), url].slice(-CONSUMED_MAX)
    window.sessionStorage.setItem(CONSUMED_KEY, JSON.stringify(next))
  } catch {
    // Private mode: the in-memory set still covers this document.
  }
}

/** How long a handled link keeps overriding the cold-launch route restore. */
const HANDLED_WINDOW_MS = 10_000

/**
 * True right after a deep link navigated (or is waiting to). The cold-launch
 * redirect (`NativeRedirect`) checks it so restoring the last route never
 * overrides the link that opened the app.
 */
export function hasHandledDeepLink(): boolean {
  return handledAt > 0 && Date.now() - handledAt < HANDLED_WINDOW_MS
}

/** Open an accepted target: route handlers (graduation) need a real page load. */
function navigateTo(router: RouterLike, target: string): void {
  if (isDocumentRoute(target.split(/[?#]/, 1)[0]) && typeof window !== 'undefined') {
    window.location.assign(target)
    return
  }
  router.push(target)
}

function handleIncomingUrl(url: string, fromLaunch = false): void {
  // The return of "Entrar con LinkedIn" from the in-app browser (ALU-519):
  // not a screen but the code to exchange, which must load in this WebView
  // because the PKCE verifier cookie is here.
  const authReturn = nativeAuthReturnTarget(url)
  if (authReturn) {
    // The one-time code is spent by the first load: the same link again (a
    // retained event, the launch URL after our own reload) must do nothing.
    // The launch path already marked it before calling here.
    if (!fromLaunch && wasConsumed(url)) return
    const now = Date.now()
    if (lastHandled && lastHandled.url === url && now - lastHandled.at < DUPLICATE_WINDOW_MS) return
    lastHandled = { url, at: now }
    handledAt = now
    markConsumed(url)
    void closeInAppBrowser()
    // replace, not assign: the callback is not a page to come back to with Back.
    if (typeof window !== 'undefined') window.location.replace(authReturn)
    return
  }

  const target = deepLinkTarget(url)
  if (!target) return
  const now = Date.now()
  if (lastHandled && lastHandled.url === url && now - lastHandled.at < DUPLICATE_WINDOW_MS) return
  lastHandled = { url, at: now }
  handledAt = now
  if (activeRouter) navigateTo(activeRouter, target)
  else pendingTarget = target
}

async function registerNativeListeners(): Promise<void> {
  const { App } = await import('@capacitor/app')

  // Warm start: a link tapped while the app is running (and, on both
  // platforms, the launch link if the plugin retained it until now).
  await App.addListener('appUrlOpen', ({ url }) => handleIncomingUrl(url))

  // Cold start: the link that launched the app.
  // It is the same link on every read until the process dies, so it is acted
  // on once: after a reload of this very page it must not be replayed.
  const launch = await App.getLaunchUrl()
  if (launch?.url && !wasConsumed(launch.url)) {
    markConsumed(launch.url)
    handleIncomingUrl(launch.url, true)
  }
}

/**
 * Initialize the deep link handling. Safe to call more than once (React
 * Strict Mode, Fast Refresh, a remount): the native listener is registered
 * a single time for the life of the WebView, and each call only points it at
 * the current router.
 *
 * @returns Cleanup function that detaches this router
 */
export async function initDeepLinking(router: RouterLike): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  const owner = Symbol('deep-link-router')
  activeRouter = router
  activeOwner = owner
  if (pendingTarget) {
    const target = pendingTarget
    pendingTarget = null
    navigateTo(router, target)
  }

  try {
    registration ??= registerNativeListeners().catch((error) => {
      // Deep linking is non-critical — degrade gracefully, and allow a retry.
      registration = null
      throw error
    })
    await registration
  } catch {
    // Not available (plugin missing): nothing to clean up.
  }

  return () => {
    // React Strict Mode (and any remount) calls this twice with the SAME
    // router; the first call's cleanup runs after the second already took over
    // and must not unplug it, or later links would wait in pendingTarget forever.
    if (activeOwner === owner) {
      activeRouter = null
      activeOwner = null
    }
  }
}

/** Test seam: forget the registration state. */
export function resetDeepLinkingForTests(): void {
  activeRouter = null
  activeOwner = null
  consumedInMemory.clear()
  try {
    window.sessionStorage.removeItem(CONSUMED_KEY)
  } catch {
    // no storage in this environment
  }
  pendingTarget = null
  registration = null
  lastHandled = null
  handledAt = 0
}

/**
 * Handle the app being resumed from background.
 * Useful for refreshing stale data.
 *
 * @returns Cleanup function to remove listener
 */
export async function onAppResume(callback: () => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('resume', callback)
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}

/**
 * Handle the app going to the background (the counterpart of `onAppResume`).
 *
 * @returns Cleanup function to remove listener
 */
export async function onAppPause(callback: () => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('pause', callback)
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}

/**
 * Handle back button press (Android only).
 * Return `true` to prevent default behavior.
 *
 * @returns Cleanup function to remove listener
 */
export async function onBackButton(
  callback: (canGoBack: boolean) => void | Promise<void>
): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('backButton', ({ canGoBack }) => {
      callback(canGoBack)
    })
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}
